import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../../../auth/AuthProviderUnified';
import { Reveal } from './LandingMotion';
import { IntelligentBackdrop } from './IntelligentBackdrop';

// Motion-wrapped react-router Link so the primary CTA can carry the breathe loop.
const MotionLink = motion.create(Link);

export const FinalCtaSection = () => {
  const { user } = useAuth();
  const reduce = useReducedMotion();

  // Gentle orange glow "breathe" for the primary CTA — disabled under reduced motion.
  const breathe = reduce
    ? undefined
    : {
        boxShadow: [
          '0 0 30px rgba(249,115,22,0.4)',
          '0 0 46px rgba(249,115,22,0.6)',
          '0 0 30px rgba(249,115,22,0.4)',
        ],
      };
  const breatheTransition = reduce
    ? undefined
    : { duration: 2.5, repeat: Infinity, ease: 'easeInOut' };
  const primaryHover = { scale: 1.03, transition: { duration: 0.2 } };

  return (
    <section className="py-24 md:py-32 relative overflow-hidden">
      <IntelligentBackdrop />
      {/* Background Glow — very slow, subtle breathe. Centering lives in framer style
          (x/y: -50%) so the animated scale composes instead of clobbering the transform. */}
      <motion.div
        className="absolute top-1/2 left-1/2 w-[60%] h-[60%] bg-orange-500/10 blur-[120px] rounded-full pointer-events-none"
        style={{ x: '-50%', y: '-50%' }}
        animate={reduce ? undefined : { scale: [1, 1.08, 1], opacity: [0.85, 1, 0.85] }}
        transition={reduce ? undefined : { duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      />

      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <Reveal blur>
            <h2 className="text-4xl md:text-6xl font-bold text-[#1C1C1E] mb-8 tracking-tight">
                Ready to put <span className="text-orange-500">Bolt</span> on your jobsite?
            </h2>
            <p className="text-lg md:text-xl text-slate-600 mb-12 leading-relaxed max-w-2xl mx-auto">
                Join the contractors using Virtual Hardhat to answer faster, automate the busywork, and keep crews building.
            </p>
        </Reveal>

        <Reveal delay={0.2}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                {user ? (
                  // Authenticated users see "Go to Dashboard" button
                  <MotionLink
                      to="/command-center"
                      className="group w-full sm:w-auto px-8 py-4 bg-orange-600 hover:bg-orange-500 text-white rounded-full font-bold text-lg transition-colors duration-300 flex items-center justify-center gap-2"
                      animate={breathe}
                      transition={breatheTransition}
                      whileHover={primaryHover}
                  >
                      Go to Dashboard
                      <LayoutDashboard size={18} className="group-hover:translate-x-1 transition-transform" />
                  </MotionLink>
                ) : (
                  // Unauthenticated users see "Request Demo" button
                  <MotionLink
                      to="/signup"
                      className="group w-full sm:w-auto px-8 py-4 bg-orange-600 hover:bg-orange-500 text-white rounded-full font-bold text-lg transition-colors duration-300 flex items-center justify-center gap-2"
                      animate={breathe}
                      transition={breatheTransition}
                      whileHover={primaryHover}
                  >
                      Request Demo
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </MotionLink>
                )}
                <Link
                    to="/solutions/contractors"
                    className="group w-full sm:w-auto px-8 py-4 bg-black/[0.04] hover:bg-black/[0.06] text-[#1C1C1E] border border-black/10 hover:border-black/15 rounded-full font-medium text-lg transition-all duration-300 flex items-center justify-center gap-2"
                >
                    View Solutions
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
            </div>
        </Reveal>
      </div>
    </section>
  );
};
